type SavedEntry = {
  page_id: string;
  name: string;
  selected: boolean;
};

/**
 * Match a page against saved selection. Looks up by page_id first (exact
 * match), then falls back to name matching for when page IDs change across
 * commits. When no match is found, returns `defaultSelected` (true unless the
 * caller overrides it — e.g. unchanged pages default to unselected).
 */
export function matchSavedSelection(
  savedSelection: Record<string, SavedEntry>,
  pageId: string,
  pageName: string,
  defaultSelected: boolean = true,
): boolean {
  // Direct lookup by page_id key.
  const byId = savedSelection[pageId];
  if (byId) return byId.selected !== false;

  // Fallback: search by name for when page IDs changed after a push.
  const byName = Object.values(savedSelection).find((e) => e.name === pageName);
  if (byName) return byName.selected !== false;

  // No match — fall back to the caller-provided default.
  return defaultSelected;
}

/**
 * For KiCad schematics, crysknife emits an empty page name. Fall back to
 * the file stem (e.g. "mikoto.kicad_sch" → "mikoto"), matching what the
 * connections-checker uses.
 */
export function resolvePageName(pageName: string, filePath: string): string {
  if (pageName) return pageName;
  const parts = filePath.split('/');
  const fileName = parts[parts.length - 1];
  const dotIndex = fileName.indexOf('.');
  return dotIndex > 0 ? fileName.substring(0, dotIndex) : fileName;
}

/**
 * Parse a page range string like "1-3, 5, 7-9" into a Set of 1-based
 * page numbers.
 */
export function parsePageRanges(input: string, maxPage: number): Set<number> {
  const result = new Set<number>();
  for (const part of input.split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const rangeParts = trimmed.split('-').map((s) => parseInt(s.trim(), 10));
    if (rangeParts.length === 1 && !Number.isNaN(rangeParts[0])) {
      const n = rangeParts[0];
      if (n >= 1 && n <= maxPage) result.add(n);
    } else if (rangeParts.length === 2 && !Number.isNaN(rangeParts[0]) && !Number.isNaN(rangeParts[1])) {
      const start = Math.max(1, rangeParts[0]);
      const end = Math.min(maxPage, rangeParts[1]);
      for (let i = start; i <= end; i++) result.add(i);
    }
  }
  return result;
}

/**
 * Build a compact page range string from checked page numbers.
 * e.g. [1,2,3,5] → "1-3, 5"
 */
export function buildPageRangeString(numbers: number[]): string {
  if (numbers.length === 0) return '';
  const sorted = Array.from(numbers).sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let end = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = sorted[i];
      end = sorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
}
